<div class="flex flex-col p-6">
  <div class="desktop-title text-primary text-display-md mb-6 font-semibold">Profile</div>

  <div class="flex max-w-md flex-col gap-6">
    <!-- Profile Picture Section -->
    <div class="flex justify-between">
      <div class="relative">
        <div class="text-md text-primary mb-2 gap-1 font-semibold">Profile Picture</div>
        <dojo-image-crop-file-input
          #fileInput
          (uploaded)="updateProfilePicture($event)"
          [storageName]="AVATARS_STORAGE_BUCKET_NAME"
          [maxFileSize]="MAX_FILE_SIZE"
        />

        @if (state.profilePicUrl || state.oAuthProfilePicUrl) {
          <div class="group relative h-[6rem] w-[6rem]">
            <img
              dojoImagePreview
              class="h-full rounded-full"
              [src]="state.profilePicUrl ?? state.oAuthProfilePicUrl ?? ''"
            />
            <div class="absolute right-1.5 top-1.5 hidden gap-1 group-hover:flex">
              <dojo-icon-button
                class="edit-icon-button"
                (click)="triggerProfilePictureUpload()"
                [size]="'xs'"
                [icon]="'pen'"
                [iconSize]="12"
              />

              @if (state.profilePicUrl) {
                <dojo-icon-button
                  class="edit-icon-button"
                  (click)="removeProfilePicture()"
                  [size]="'xs'"
                  [icon]="'close'"
                />
              }
            </div>
          </div>
        } @else {
          <div class="add-new">
            <dojo-button
              class="add-new-button block h-[6rem] w-[6rem]"
              [fill]="'outline'"
              (click)="triggerProfilePictureUpload()"
            >
              <div class="flex flex-col items-center text-neutral-500">
                <dojo-icon icon="camera" [size]="28" class="mb-1" />
                <span class="text-sm font-semibold">Add Picture</span>
              </div>
            </dojo-button>
          </div>
        }
      </div>
      <div class="flex items-center gap-1 self-start">
        <!-- Save indicators removed - using manual save buttons instead -->
      </div>
    </div>

    <!-- User Information Form -->
    <form [formGroup]="userForm" (ngSubmit)="saveUserInfo()" class="flex flex-col gap-4">
      <dojo-text-input
        label="First Name"
        [formCtrl]="userForm.controls.first_name"
        placeholder="First Name"
      ></dojo-text-input>
      <dojo-text-input
        label="Last Name"
        [formCtrl]="userForm.controls.last_name"
        placeholder="Last Name"
      ></dojo-text-input>
      <div class="flex flex-col gap-2">
        <label class="text-md font-semibold text-gray-700">Email</label>
        <dojo-text-input [formCtrl]="userForm.controls.email" [type]="'email'" placeholder="Email"></dojo-text-input>
      </div>

      @if (emailUpdatePending()) {
        <div class="rounded-[1rem] border border-solid border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-700">
          Please check your new email address to confirm the change.
        </div>
      }

      <div class="flex items-center gap-2">
        <dojo-button
          type="submit"
          [disabled]="!userForm.valid || !userForm.dirty || isSavingUserInfo()"
          [pending]="isSavingUserInfo()"
        >
          Save Changes
        </dojo-button>
      </div>
    </form>

    <!-- Social Accounts Section -->
    <div class="border-t border-gray-200 pt-6">
      <h2 class="mb-4 text-lg font-semibold text-gray-800">Accounts</h2>

      <div class="text-primary flex flex-col gap-4">
        <!-- Google Provider -->
        <mat-slide-toggle
          [checked]="state.isGoogleProviderConnected"
          (change)="onToggleChange(OidcProvider.Google, $event)"
          color="primary"
          class="small full-row w-full"
          labelPosition="before"
        >
          <div class="flex items-center gap-2">
            <div class="flex h-5 w-5 items-center justify-center">
              @if (state.isGoogleLoading) {
                <mat-spinner [diameter]="20" color="primary" />
              } @else {
                <img class="h-full w-full" src="./assets/images/google-logo.png" alt="Google" />
              }
            </div>
            <span>Google</span>
            @if (state.currentLoginProvider === 'google') {
              <span
                class="text-neutral-0 ml-2 inline-flex rounded-[6px] bg-sky-500 px-1.5 py-[1px] text-sm font-semibold"
                >Signed in</span
              >
            }
          </div>
        </mat-slide-toggle>

        <!-- Discord Provider -->
        <mat-slide-toggle
          [checked]="state.isDiscordProviderConnected"
          (change)="onToggleChange(OidcProvider.Discord, $event)"
          color="primary"
          class="small full-row w-full"
          labelPosition="before"
        >
          <div class="flex items-center gap-2">
            <div class="flex h-5 w-5 items-center justify-center">
              @if (state.isDiscordLoading) {
                <mat-spinner [diameter]="20" color="primary" />
              } @else {
                <img class="h-full w-full" src="./assets/images/discord-logo.svg" alt="Discord" />
              }
            </div>
            <span>Discord</span>
            @if (state.currentLoginProvider === 'discord') {
              <span
                class="text-neutral-0 ml-2 inline-flex rounded-[6px] bg-sky-500 px-1.5 py-[1px] text-sm font-semibold"
                >Signed in</span
              >
            }
          </div>
        </mat-slide-toggle>

        <!-- Password Provider -->
        <mat-slide-toggle
          [checked]="state.isPasswordProviderConnected"
          [disabled]="true"
          color="primary"
          class="small full-row w-full"
          labelPosition="before"
        >
          <div class="flex items-center gap-2">
            <div class="flex h-5 w-5 items-center justify-center">
              @if (state.isPasswordLoading) {
                <mat-spinner [diameter]="20" color="primary" />
              } @else {
                <dojo-icon [icon]="'lock'" class="text-gray-700" />
              }
            </div>
            <span>Password</span>
            @if (state.currentLoginProvider === 'password') {
              <span
                class="text-neutral-0 ml-2 inline-flex rounded-[6px] bg-sky-500 px-1.5 py-[1px] text-sm font-semibold"
                >Signed in</span
              >
            }
          </div>
        </mat-slide-toggle>
      </div>
    </div>
    <!-- Password Change Section -->
    <div class="mb-6 border-t border-gray-200 pt-6">
      <!-- Password Change Section -->
      <h2 class="mb-4 text-lg font-semibold text-gray-800">Change Password</h2>

      <form [formGroup]="passwordForm" (ngSubmit)="changePassword()" class="flex flex-col gap-4">
        <dojo-text-input
          label="New Password"
          [formCtrl]="passwordForm.controls.newPassword"
          [type]="'password'"
          placeholder="Enter new password (min 6 characters)"
        ></dojo-text-input>

        <dojo-text-input
          label="Confirm New Password"
          [formCtrl]="passwordForm.controls.confirmPassword"
          [type]="'password'"
          placeholder="Confirm new password"
          [errorMessages]="confirmPasswordErrorMessages"
        ></dojo-text-input>

        <div class="mb-6 flex items-center gap-2">
          <dojo-button
            type="submit"
            [disabled]="!isPasswordFormValid || isSavingPassword()"
            [pending]="isSavingPassword()"
          >
            Change Password
          </dojo-button>
        </div>
      </form>
    </div>
  </div>
</div>
